import { z } from 'zod';

import { ARTIFACT_TYPES, UNKNOWN_TRANSITION_MODES } from '../constants/enums';

export const contourRuntimePolicySchema = z.object({
  contour_id: z.string().min(1),
  contour_name: z.string().min(1),
  object_type: z.enum(ARTIFACT_TYPES).nullable(),
  rigidity_level: z.number().int().min(0),
  auto_transition: z.number().int().min(0).max(1),
  unknown_transition: z.enum(UNKNOWN_TRANSITION_MODES),
  runtime_mode: z.enum(['strong', 'weak', 'hybrid']),
  modes: z.array(z.string().min(1)),
});

export const contourRuntimePhaseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sort_order: z.number().int().nullable(),
  is_start: z.number().int().min(0).max(1),
  is_terminal: z.number().int().min(0).max(1),
  position_count: z.number().int().min(0),
  active_count: z.number().int().min(0),
  proposal_count: z.number().int().min(0),
});

export const contourRuntimePositionSchema = z.object({
  id: z.string().min(1),
  process_id: z.string().min(1).optional(),
  artifact_id: z.string().min(1),
  phase_id: z.string().min(1),
  phase_label: z.string().min(1),
  contour_instance_id: z.string().nullable(),
  membership_score: z.number().min(0).max(1),
  confidence_score: z.number().min(0).max(1).nullable(),
  is_primary: z.number().int().min(0).max(1),
  entered_at: z.string().nullable(),
  updated_at: z.string().min(1),
});

export const contourRuntimeInstanceSchema = z.object({
  id: z.string().min(1),
  artifact_id: z.string().min(1),
  contour_package_id: z.string().min(1),
  current_phase_id: z.string().nullable(),
  current_phase_label: z.string().nullable(),
  status: z.enum(['active', 'completed', 'abandoned']),
  started_at: z.string().min(1),
  completed_at: z.string().nullable(),
});

export const contourRuntimeTransitionSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  label: z.string().min(1),
  rule_id: z.string().min(1),
});

export const contourRuntimeProposalSchema = z.object({
  id: z.string().min(1),
  artifact_id: z.string().min(1),
  contour_package_id: z.string().min(1),
  from_phase_id: z.string().nullable(),
  from_phase_label: z.string().nullable(),
  to_phase_id: z.string().min(1),
  to_phase_label: z.string().min(1),
  status: z.enum(['pending', 'approved', 'rejected', 'expired', 'executed']),
  proposer_type: z.string().min(1),
  decision_id: z.string().nullable(),
  created_at: z.string().min(1),
});

export const contourRuntimeLaneSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  kind: z.enum(['phase', 'mode']),
  is_start: z.boolean(),
  is_terminal: z.boolean(),
  position_ids: z.array(z.string().min(1)),
  proposal_ids: z.array(z.string().min(1)),
});

export const contourRuntimeLockSchema = z.object({
  strategy: z.enum(['none', 'durable_object']),
  serialized_transition_path: z.boolean(),
});

export const contourRuntimeSummarySchema = z.object({
  phase_count: z.number().int().min(0),
  mode_count: z.number().int().min(0),
  transition_count: z.number().int().min(0),
  active_instances: z.number().int().min(0),
  positioned_artifacts: z.number().int().min(0),
  pending_proposals: z.number().int().min(0),
});

const graphNodeSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  kind: z.literal('phase'),
  is_start: z.boolean().optional(),
  is_terminal: z.boolean().optional(),
});

const graphSchema = z.object({
  mermaid: z.string().min(1),
  nodes: z.array(graphNodeSchema),
  edges: z.array(contourRuntimeTransitionSchema),
});

export const contourRuntimePacketSchema = z
  .object({
    policy: contourRuntimePolicySchema,
    graph: graphSchema,
    phases: z.array(contourRuntimePhaseSchema),
    positions: z.array(contourRuntimePositionSchema),
    instances: z.array(contourRuntimeInstanceSchema),
    proposals: z.array(contourRuntimeProposalSchema),
    lanes: z.array(contourRuntimeLaneSchema),
    lock: contourRuntimeLockSchema,
    summary: contourRuntimeSummarySchema,
  })
  .superRefine((packet, ctx) => {
    const contourId = packet.policy.contour_id;
    const positionIds = new Set(packet.positions.map((position) => position.id));
    const proposalIds = new Set(packet.proposals.map((proposal) => proposal.id));
    const activeInstances = packet.instances.filter((instance) => instance.status === 'active').length;
    const positionedArtifacts = new Set(packet.positions.map((position) => position.artifact_id)).size;
    const pendingProposals = packet.proposals.filter((proposal) => proposal.status === 'pending').length;

    for (const [index, position] of packet.positions.entries()) {
      if (position.process_id !== contourId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['positions', index, 'process_id'],
          message: 'position must belong to the packet contour',
        });
      }
    }
    for (const [index, instance] of packet.instances.entries()) {
      if (instance.contour_package_id !== contourId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['instances', index, 'contour_package_id'],
          message: 'instance must belong to the packet contour',
        });
      }
    }
    for (const [index, proposal] of packet.proposals.entries()) {
      if (proposal.contour_package_id !== contourId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['proposals', index, 'contour_package_id'],
          message: 'proposal must belong to the packet contour',
        });
      }
    }
    for (const [laneIndex, lane] of packet.lanes.entries()) {
      for (const [idIndex, positionId] of lane.position_ids.entries()) {
        if (!positionIds.has(positionId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lanes', laneIndex, 'position_ids', idIndex],
            message: 'lane references a missing position',
          });
        }
      }
      for (const [idIndex, proposalId] of lane.proposal_ids.entries()) {
        if (!proposalIds.has(proposalId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lanes', laneIndex, 'proposal_ids', idIndex],
            message: 'lane references a missing proposal',
          });
        }
      }
    }
    assertSummaryCount(ctx, ['summary', 'phase_count'], packet.summary.phase_count, packet.phases.length);
    assertSummaryCount(ctx, ['summary', 'mode_count'], packet.summary.mode_count, packet.policy.modes.length);
    assertSummaryCount(ctx, ['summary', 'transition_count'], packet.summary.transition_count, packet.graph.edges.length);
    assertSummaryCount(ctx, ['summary', 'active_instances'], packet.summary.active_instances, activeInstances);
    assertSummaryCount(ctx, ['summary', 'positioned_artifacts'], packet.summary.positioned_artifacts, positionedArtifacts);
    assertSummaryCount(ctx, ['summary', 'pending_proposals'], packet.summary.pending_proposals, pendingProposals);
  });

function assertSummaryCount(
  ctx: z.RefinementCtx,
  path: (string | number)[],
  actual: number,
  expected: number,
): void {
  if (actual === expected) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    message: `summary count must equal ${expected}`,
  });
}

export type ContourRuntimePacket = z.infer<typeof contourRuntimePacketSchema>;
